import { VerificationType } from '@logto/schemas';
import { conditional } from '@silverhand/essentials';
import { WebAuthnAbortService } from '@simplewebauthn/browser';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { validate } from 'superstruct';

import SecondaryPageLayout from '@/Layout/SecondaryPageLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import SwitchMfaFactorsLink from '@/components/SwitchMfaFactorsLink';
import useSkipMfa from '@/hooks/use-skip-mfa';
import useSkipOptionalMfa from '@/hooks/use-skip-optional-mfa';
import useWebAuthnOperation from '@/hooks/use-webauthn-operation';
import ErrorPage from '@/pages/ErrorPage';
import Button from '@/shared/components/Button';
import { UserMfaFlow } from '@/types';
import { type MfaFlowState, webAuthnStateGuard } from '@/types/guard';
import { isWebAuthnOptions } from '@/utils/webauthn';

import styles from './index.module.scss';

const WebAuthnBinding = () => {
  const { state } = useLocation();
  const [, webAuthnState] = validate(state, webAuthnStateGuard);
  const { verificationIdsMap } = useContext(UserInteractionContext);
  const verificationId = verificationIdsMap[VerificationType.WebAuthn];

  const handleWebAuthn = useWebAuthnOperation();
  const skipMfa = useSkipMfa();
  const skipOptionalMfa = useSkipOptionalMfa();
  const [isCreatingPasskey, setIsCreatingPasskey] = useState(false);

  // Guarantee the WebAuthn ceremony is cancelled when the user navigates away
  // (e.g. taps the top-bar back arrow or switches to another MFA factor).
  // Without this, a still-pending Safari authorization sheet can survive the
  // route change and block subsequent sign-in attempts with an opaque
  // "verification failed" state.
  useEffect(
    () => () => {
      try {
        WebAuthnAbortService.cancelCeremony();
      } catch {
        // Best-effort.
      }
    },
    []
  );

  if (!webAuthnState || !verificationId) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const {
    options,
    availableFactors,
    skippable,
    suggestion,
    maskedIdentifiers,
    isWebAuthnUsedAsSignInPasskey,
  } = webAuthnState;
  const mfaFlowState: MfaFlowState = {
    availableFactors,
    skippable,
    suggestion,
    maskedIdentifiers,
    isWebAuthnUsedAsSignInPasskey,
  };

  if (!isWebAuthnOptions(options)) {
    return <ErrorPage title="error.invalid_session" />;
  }

  return (
    <SecondaryPageLayout
      title="mfa.create_a_passkey"
      description="mfa.create_passkey_description"
      onSkip={conditional(skippable && (suggestion ? skipOptionalMfa : skipMfa))}
    >
      <Button
        title="mfa.create_a_passkey"
        isLoading={isCreatingPasskey}
        onClick={async () => {
          setIsCreatingPasskey(true);
          await handleWebAuthn(options, verificationId);
          setIsCreatingPasskey(false);
        }}
      />
      <SwitchMfaFactorsLink
        flow={UserMfaFlow.MfaBinding}
        flowState={mfaFlowState}
        className={styles.switchLink}
      />
    </SecondaryPageLayout>
  );
};

export default WebAuthnBinding;
